import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject, input, model, viewChild, output } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { of, ReplaySubject, Subject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';

import {
  ApplicationSettingService,
  BaseEditService,
  BaseImportService,
  MenuService,
  NotificationService,
  RouterService,
  SystemEnvironmentService,
  UserPreferenceService,
} from '../../../services';
import { FormlyService } from '../../../services/component-services/formly.service';
import { GridLayoutFormService } from '../../../services/component-services/grid-layout-form.service';
import {
  FormlyButtonDropdown,
  GridRowSelect,
  Search,
  SearchGridSearched,
  SearchPage,
  UserPreference,
} from '../../../models';
import { MockEditService } from '../../../mock';
import { GridComponent } from '../grid/grid.component';
import { BaseComponent } from '../../base/base.component';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';

@Component({
  selector: 'app-search-grid',
  templateUrl: './search-grid.component.html',
  styleUrl: './search-grid.component.scss',
  imports: [BaseInlineFormComponent, GridComponent, AsyncPipe],
})
export class SearchGridComponent extends BaseComponent implements OnInit {
  private readonly formlyService = inject(FormlyService);
  private readonly routerService = inject(RouterService);
  private readonly route = inject(ActivatedRoute);
  private readonly applicationSettingService = inject(ApplicationSettingService);
  private readonly gridLayoutFormService = inject(GridLayoutFormService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly menuService = inject(MenuService);
  private readonly title = inject(Title);
  private readonly systemEnvironmentService = inject(SystemEnvironmentService);
  private readonly notificationService = inject(NotificationService);

  protected create$ = new Subject<void>();
  protected delete$ = new Subject<any>();
  protected edit$ = new Subject<any>();

  public readonly form = viewChild(BaseInlineFormComponent);
  public readonly grid = viewChild(GridComponent);

  public readonly stateKey = input<string>();
  public readonly header = input<string>();
  public readonly dropdownText = input($localize`Download Template`);
  public readonly service = input<BaseEditService<any, any>>(new MockEditService());
  public readonly importService = input<BaseImportService>();
  public readonly importButtonText = input($localize`Import`);
  public readonly searchLabel = input('');
  public readonly showCreate = input(true);
  public readonly showDelete = input(true);
  public readonly showDeleteMessage = input(true);
  public readonly showBack = input(false);
  public readonly showExport = input(true);
  public readonly showPrint = input(false);
  public readonly showPrintTop = input(false);
  public readonly showLayouts = input(false);
  public readonly retainState = input(false);
  // The user groups the rows themselves - see app-grid's rowGrouping.
  public readonly rowGrouping = input(false);
  public readonly showReport = input(false);
  public readonly showReport2 = input(false);
  public readonly reportButtonText = input($localize`Report`);
  public readonly report2ButtonText = input<string>();
  public readonly showFromToDate = input(false);
  public readonly showFromToDateCondition = input<(model: any) => boolean>();
  public readonly showIncludeAll = input(false);
  public readonly includeAllText = input($localize`Include All`);
  public readonly showBool1 = input(false);
  public readonly bool1Text = input<string>();
  public readonly showBool2 = input(false);
  public readonly bool2Text = input<string>();
  public readonly allowIncludeAllBlankSearch = input(false);
  public readonly blankSearchSetting = input<string>();
  public readonly allowNavigateOnSingleResult = input(true);
  // Optional, per screen: a single result opens only when this also says so for that row and
  // the text searched - e.g. Transfers opens a pallet on an EXACT number, and lists on a partial
  // one. Unset, every list keeps the plain single-result rule.
  public readonly navigateOnSingleResultWhen = input<(row: any, search: string) => boolean>();
  public readonly searchOnModelChange = input(true);
  public readonly allowEdit = input(true);
  public readonly gridCssClass = input<string>();
  public readonly columnDefs = input<(ColDef | ColGroupDef)[]>([]);
  public readonly initialSearch = input(true);
  public readonly allowInitialBlankSearch = input(true);
  public readonly advancedSearchTooltip = input(false);
  public readonly filter = input(true);
  public readonly resizable = input(true);
  public readonly sortable = input(true);
  public readonly additionalFields = input<FormlyFieldConfig[]>([]);
  public readonly additionalFieldsLeft = input<FormlyFieldConfig[]>([]);
  public readonly additionalFieldsRight = input<FormlyFieldConfig[]>([]);
  public readonly id = input<string>();
  public readonly id2 = input<string>();
  public readonly labelsVertical = input(false);
  public readonly fieldsVertical = input<boolean>();
  public readonly createDropdown = input<FormlyButtonDropdown>();
  public readonly importDropdown = input<FormlyButtonDropdown>();
  public readonly importSelect = input<(file: any) => void>();
  public readonly printDropdown = input<FormlyButtonDropdown>();
  public readonly saveSearch = input(true);
  // Values laid over the restored saved search before the first search runs - another screen handing
  // this list its filters through query parameters (the Sales Order Board's "Open in Exceptions
  // list"). Unset, the saved search is used as it is.
  public readonly initialModel = input<Partial<Search> & Record<string, any>>();
  public readonly serverSide = input(false);
  public readonly pageSize = input(100);
  public readonly create = outputFromObservable<void>(this.create$);
  public readonly delete = outputFromObservable<any>(this.delete$);
  public readonly edit = outputFromObservable<any>(this.edit$);
  public readonly print = output<any>();
  public readonly printTop = output<void>();
  public readonly rowSelected = output<GridRowSelect>();
  // Each search's text and row count, as the results land - client-side and server-side alike.
  public readonly searched = output<SearchGridSearched>();

  protected fields: FormlyFieldConfig[] = [];
  public rowData$: Observable<any> = of([]);
  public model: Search = {} as Search;

  private allowBlankSearch = false;

  private gridReadySubject = new ReplaySubject<void>(1);

  public firstDataRenderedSubject = new ReplaySubject<void>(1);
  public firstDataRendered$ = this.firstDataRenderedSubject.asObservable();

  protected readyForSearch = false;
  private serverSideParams: IServerSideGetRowsParams;

  ngOnInit() {
    this.checkForBlankSearch();

    if (this.header()) {
      this.title.setTitle(this.systemEnvironmentService.pageTitle(this.header()));
    }
  }

  refresh() {
    this.grid().loadState();
    this.onSearch();
  }

  updateForm() {
    this.form().update();
  }

  protected onSearch(navigateOnSingleResult = false) {
    if (this.serverSide()) {
      this.grid().gridApi.refreshServerSide({ purge: navigateOnSingleResult });
    } else {
      this.setResults(
        this.service().search({
          ...this.model,
          search: this.model.search !== '' ? this.model.search : this.form()?.model()?.search,
          id: this.id() ?? this.model.id,
          id2: this.id2() ?? this.model.id2,
          allowIncludeAllBlankSearch: this.allowIncludeAllBlankSearch(),
          allowBlankSearch: this.allowBlankSearch,
        }),
        navigateOnSingleResult,
      );
    }
  }

  protected serverSideSearch(params: IServerSideGetRowsParams, navigateOnSingleResult = false) {
    if (!this.readyForSearch) {
      this.serverSideParams = params;
      return;
    }

    this.service()
      .searchPage({
        ...this.model,
        id: this.id() ?? this.model.id,
        id2: this.id2() ?? this.model.id2,
        allowIncludeAllBlankSearch: this.allowIncludeAllBlankSearch(),
        allowBlankSearch: this.allowBlankSearch,
        serverSideSearch: params.request,
      })
      .pipe(this.untilDestroyed())
      .subscribe((searchPage: SearchPage<any>) => {
        this.saveSearchModel();
        params.success({
          rowData: searchPage.results,
          rowCount: searchPage.count,
        });
        this.searched.emit({ search: this.searchText(), count: searchPage.count });
        if (
          this.model.search &&
          this.allowNavigateOnSingleResult() &&
          navigateOnSingleResult &&
          searchPage.count === 1
        ) {
          this.onEdit(searchPage.results[0]);
        }
      });
  }

  protected onCreate() {
    if (this.create$.observed) {
      this.create$.next();
    } else {
      this.routerService.create(this.route);
    }
  }

  protected onEdit(model: any) {
    if (this.edit$.observed) {
      this.edit$.next(model.id);
    } else {
      if (this.allowEdit()) {
        this.routerService.setPathIds(this.grid().sortedAndFilteredRowIds);
        this.routerService.edit(model.id, this.route);
      }
    }
  }

  protected onDelete(model: any) {
    if (this.delete$.observed) {
      this.delete$.next(model);
    } else {
      // A refused delete says why. It was swallowed, so a refusal looked like nothing happening -
      // e.g. cancelling an order whose lines have been despatched (4Spaces, 24 Sep 2026).
      this.service()
        .delete(model.id)
        .pipe(this.untilDestroyed())
        .subscribe({
          next: () => this.onSearch(),
          error: (error) => this.notificationService.apiFail(error, $localize`Delete failed`),
        });
    }
  }

  protected imported() {
    this.onSearch();
  }

  protected report() {
    this.routerService.report(this.route);
  }

  protected report2() {
    this.routerService.report2(this.route);
  }

  protected get gridStateKey() {
    return `Search.${this.stateKey() ?? this.header()}`;
  }

  protected get layoutStateKey(): string | undefined {
    if (!this.showLayouts() || !this.grid() || !this.model?.layout) {
      return undefined;
    }

    const layout = this.grid().layouts.find((userPreference) => userPreference.id === this.model.layout);
    return layout?.key;
  }

  private checkForBlankSearch() {
    if (this.blankSearchSetting()) {
      this.applicationSettingService
        .getBoolean(this.blankSearchSetting())
        .pipe(this.untilDestroyed())
        .subscribe((allowBlankSearch: boolean) => {
          this.allowBlankSearch = allowBlankSearch;
          this.getFields();
          this.performInitialSearch();
        });
    } else {
      this.allowBlankSearch = true;
      this.getFields();
      this.performInitialSearch();
    }
  }

  private performInitialSearch() {
    this.userPreferenceService
      .get(this.gridStateKey)
      .pipe(this.untilDestroyed())
      .subscribe((userPreference: UserPreference) => {
        if (userPreference.value?.startsWith('{')) {
          this.model = JSON.parse(userPreference.value);
        } else {
          this.model.search = userPreference.value;
        }
        if (this.initialModel()) {
          this.model = { ...this.model, ...this.initialModel() };
        }
        this.readyForSearch = true;
        if (
          (this.initialSearch() || this.route.snapshot.queryParams.back) &&
          (this.model.search || this.model.id || this.model.id2 || this.allowInitialBlankSearch())
        ) {
          if (this.serverSide() && this.serverSideParams) {
            this.serverSideSearch(this.serverSideParams);
          } else {
            this.onSearch();
          }
        } else if (this.serverSide() && this.serverSideParams) {
          this.serverSideParams.success({
            rowData: [],
            rowCount: 0,
          });
        }
      });
  }

  private getFields() {
    const fields: FormlyFieldConfig[] = [];

    if (this.showBack()) {
      fields.push(this.formlyService.backButton(this.route));
    }

    if (this.additionalFieldsLeft()) {
      fields.push(...this.additionalFieldsLeft());
    }

    fields.push(
      this.formlyService.search(
        'search',
        this.searchLabel(),
        this.service(),
        (results: Observable<any[]>) => this.setResults(results, true),
        {
          id: this.id(),
          id2: this.id2(),
          allowIncludeAllBlankSearch: this.allowIncludeAllBlankSearch(),
          allowBlankSearch: this.allowBlankSearch,
          advancedSearchTooltip: this.advancedSearchTooltip(),
          search: () => this.onSearch(true),
        },
      ),
    );

    if (this.showCreate() && !this.menuService.isReadOnly) {
      fields.push(this.formlyService.createButton(() => this.onCreate(), { dropdown: this.createDropdown() }));
    }

    if (this.showExport()) {
      fields.push(this.formlyService.exportButton(this.grid(), this.header()));
    }

    if (this.importService() && !this.menuService.isReadOnly) {
      if (this.importDropdown()) {
        fields.push(
          this.formlyService.importButton(
            this.importButtonText(),
            (file: any) => {
              if (this.importSelect()) {
                this.importSelect()(file);
              } else {
                this.grid().import(file);
              }
            },
            {
              dropdown: this.importDropdown(),
            },
          ),
        );
      } else {
        fields.push(
          this.formlyService.fileUpload(
            this.importButtonText(),
            (file: any) => this.grid().import(file),
            () => this.grid().exportFile(),
            { dropdownText: this.dropdownText() },
          ),
        );
      }
    }

    if (this.showPrint()) {
      fields.push(this.formlyService.printButton(() => this.printTop.emit(), { dropdown: this.printDropdown() }));
    }

    if (this.showReport()) {
      fields.push(
        this.formlyService.button(this.reportButtonText(), () => this.report(), {
          btnClass: 'secondary',
          iconClass: 'fa fa-file-invoice',
        }),
      );
    }

    if (this.showReport2()) {
      fields.push(
        this.formlyService.button(this.report2ButtonText(), () => this.report2(), {
          btnClass: 'secondary',
          iconClass: 'fa fa-file-invoice',
        }),
      );
    }

    if (this.additionalFields()) {
      fields.push(...this.additionalFields());
    }

    if (this.showFromToDate()) {
      fields.push(
        this.formlyService.date(
          'fromDate',
          $localize`From Date`,
          {
            onChange: () => this.searchModelChanged(),
          },
          this.showFromToDateCondition() ? () => !this.showFromToDateCondition()(this.model) : undefined,
        ),
        this.formlyService.date(
          'toDate',
          $localize`To Date`,
          {
            onChange: () => this.searchModelChanged(),
          },
          this.showFromToDateCondition() ? () => !this.showFromToDateCondition()(this.model) : undefined,
        ),
      );
    }

    if (this.showIncludeAll()) {
      fields.push(
        this.formlyService.checkbox('includeAll', this.includeAllText(), {
          onChange: () => this.searchModelChanged(),
        }),
      );
    }

    if (this.showBool1()) {
      fields.push(
        this.formlyService.checkbox('bool1', this.bool1Text(), {
          onChange: () => this.searchModelChanged(),
        }),
      );
    }

    if (this.showBool2()) {
      fields.push(
        this.formlyService.checkbox('bool2', this.bool2Text(), {
          onChange: () => this.searchModelChanged(),
        }),
      );
    }

    if (this.showLayouts()) {
      fields.push(
        this.gridLayoutFormService.createLayoutField({
          grid: this.grid(),
          form: this.form(),
          model: this.model,
        }),
      );
    }

    if (this.additionalFieldsRight()) {
      fields.push(...this.additionalFieldsRight());
    }

    this.fields = fields;
  }

  private searchModelChanged() {
    if (this.searchOnModelChange()) {
      this.onSearch();
    }
  }

  private setResults(results: Observable<any[]>, navigateOnSingleResult = false) {
    this.saveSearchModel();

    if (results) {
      this.rowData$ = results.pipe(
        this.untilDestroyed(),
        tap((rowData: any[]) => {
          this.searched.emit({ search: this.searchText(), count: rowData?.length ?? 0 });
          if (
            this.model.search &&
            this.allowNavigateOnSingleResult() &&
            navigateOnSingleResult &&
            rowData?.length === 1 &&
            (this.navigateOnSingleResultWhen()?.(rowData[0], this.searchText()) ?? true)
          ) {
            this.onEdit(rowData[0]);
          }
        }),
      );
    }
  }

  // The text the search actually ran with - the same fallback onSearch uses.
  private searchText(): string {
    return (this.model.search || this.form()?.model()?.search || '').toString();
  }

  private saveSearchModel() {
    if (this.saveSearch()) {
      this.userPreferenceService.set(this.gridStateKey, JSON.stringify(this.model)).subscribe();
    }
  }

  protected savedState() {
    this.grid().loadLayouts().subscribe();
  }

  onGridReady($event: any) {
    this.gridReadySubject.next();

    // The initial loadLayouts() in createLayoutField runs during ngOnInit, before the inner grid
    // is ready — and on screens with no initial search (e.g. Sales Orders) nothing retries, so
    // the layout dropdown stays empty until a layout save forces a refresh. Reload once the grid
    // is actually ready so personal + global layouts appear on screen load.
    if (this.showLayouts()) {
      this.grid()?.loadLayouts().subscribe();
    }
  }

  onFirstDataRendered() {
    this.firstDataRenderedSubject.next();
  }
}
